import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { BusyLabel } from "@/components/loading";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Settings, AlertTriangle, TrendingUp, TrendingDown, Info, Plus, Trash2, Check, DollarSign, Camera, Store } from "lucide-react";

/**
 * Editor de configuración super-admin.
 *
 * Diseño (Feb 2026):
 *  - Comisiones: tabla editable + previsualización + checkbox de confirmación
 *  - Fotos mínimas: sección aparte con su propio botón Guardar (evita commits
 *    accidentales al tocar comisiones)
 *  - Tiendas aliadas: sección aparte, endpoint dedicado
 *
 * Reglas críticas:
 *  - Nada retroactivo: solo aplica a publicaciones/checkouts FUTUROS
 *  - Alerta "regala la carta": si algún sample_price deja seller_net <= 0
 *  - Etiqueta "escenario transferencia": los números NO incluyen fee Payphone
 */

// Same defaults from admin_config.py
const DEFAULT_SAMPLE_PRICES = [0.25, 0.50, 1.00, 2.50, 3.00, 3.50, 5.00, 10.00, 30.00, 100.00, 300.00];

function SocialLinksSection() {
  // Editor de social_links para el footer del Home (Feb 2026 v11).
  // Los 3 campos se guardan en config.social_links como un dict — los lee
  // Home.jsx desde GET /config/public.
  const [links, setLinks] = useState({ instagram: "", tiktok: "", whatsapp: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config/public").then((r) => {
      setLinks({ instagram: "", tiktok: "", whatsapp: "", ...(r.data?.social_links || {}) });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", { key: "social_links", value: links });
      toast.success("Redes sociales actualizadas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (loading) return null;
  const rows = [
    ["instagram", "Instagram", "https://instagram.com/hittcg.ec"],
    ["tiktok", "TikTok", "https://tiktok.com/@hittcg"],
    ["whatsapp", "WhatsApp (link de invitación al grupo)", "https://chat.whatsapp.com/XXXX"],
  ];
  return (
    <section className="border border-[#262626] rounded-lg p-6 bg-[#0A0A0A]" data-testid="social-links-section">
      <h2 className="text-lg font-display mb-1 flex items-center gap-2">🔗 Redes sociales</h2>
      <p className="text-sm text-neutral-400 mb-4">Se muestran en el footer y en la sección "Comunidad" del Home. Deja vacío para ocultar el ícono/tarjeta correspondiente.</p>
      <div className="space-y-3">
        {rows.map(([k, label, placeholder]) => (
          <div key={k}>
            <Label className="text-xs text-neutral-400">{label}</Label>
            <Input
              data-testid={`social-input-${k}`}
              type="url"
              placeholder={placeholder}
              value={links[k] || ""}
              onChange={(e) => setLinks((l) => ({ ...l, [k]: e.target.value }))}
              className="bg-[#111] border-[#262626]"
            />
          </div>
        ))}
      </div>
      <Button
        data-testid="social-save-btn"
        onClick={save}
        disabled={saving}
        className="mt-4 bg-[#EF4444] hover:bg-[#DC2626]"
      >
        <BusyLabel busy={saving} text="Guardando…">Guardar redes sociales</BusyLabel>
      </Button>
    </section>
  );
}

export default function AdminConfigEditor() {
  return (
    <div className="space-y-8" data-testid="admin-config-editor">
      <header>
        <h1 className="text-2xl font-display flex items-center gap-2">
          <Settings size={22} className="text-[#EF4444]" /> Configuración
        </h1>
        <p className="text-sm text-neutral-400 mt-1 max-w-3xl">
          Cambios en <b className="text-white">comisiones</b> y fotos mínimas aplican solo hacia adelante — órdenes existentes conservan lo que el vendedor vio al aceptar su venta. Todos los cambios quedan registrados en el log de auditoría con el valor anterior y el nuevo.
        </p>
      </header>
      <CommissionTiersSection />
      <MinPhotosSection />
      <PartnerStoresSection />
      <SocialLinksSection />
    </div>
  );
}

// ============================================================
// COMMISSION TIERS
// ============================================================

function CommissionTiersSection() {
  const [loading, setLoading] = useState(true);
  const [savedTiers, setSavedTiers] = useState(null);    // lo que está en DB
  const [draftTiers, setDraftTiers] = useState(null);    // lo editable
  const [preview, setPreview] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customPrice, setCustomPrice] = useState("");
  const [customPrices, setCustomPrices] = useState([]);  // precios ad-hoc agregados

  const previewTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/config/public");
      const tiers = r.data?.commission_tiers || [];
      setSavedTiers(deepClone(tiers));
      setDraftTiers(deepClone(tiers));
    } catch (e) {
      toast.error("No se pudo cargar la configuración actual");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasChanges = useMemo(() => {
    return savedTiers && draftTiers && JSON.stringify(savedTiers) !== JSON.stringify(draftTiers);
  }, [savedTiers, draftTiers]);

  // Debounced preview whenever draftTiers or customPrices change
  useEffect(() => {
    if (!draftTiers || !hasChanges) { setPreview(null); setValidationError(null); return; }
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const prices = [...DEFAULT_SAMPLE_PRICES, ...customPrices].sort((a,b)=>a-b);
        const r = await api.post("/admin/config/commission-tiers/preview", {
          proposed_tiers: draftTiers,
          sample_prices: prices,
        });
        setPreview(r.data);
        setValidationError(null);
      } catch (err) {
        const detail = err?.response?.data?.detail || {};
        setPreview(null);
        setValidationError({
          code: detail.code || "unknown",
          message: detail.message || "Los tramos son inválidos",
          field: detail.field,
        });
      }
    }, 300);
    return () => clearTimeout(previewTimer.current);
  }, [draftTiers, customPrices, hasChanges]);

  const updateTier = (idx, field, rawValue) => {
    setDraftTiers((prev) => {
      const next = deepClone(prev);
      let v = rawValue;
      if (field === "max") {
        v = rawValue === "" || rawValue === null || rawValue === "∞" ? null : Number(rawValue);
      } else {
        v = rawValue === "" ? 0 : Number(rawValue);
      }
      next[idx][field] = v;
      return next;
    });
    setConfirmed(false);
  };

  const addTier = () => {
    setDraftTiers((prev) => {
      const next = deepClone(prev);
      // Insertar ANTES del último (que es max=null). Nuevo max = último_previo + 1
      const last = next[next.length - 1];
      const prevMax = next.length >= 2 ? next[next.length - 2].max : 0;
      const newMax = (prevMax || 0) + 10;
      next.splice(next.length - 1, 0, {
        max: newMax,
        pct: last.pct,
        min_fee: last.min_fee,
      });
      return next;
    });
    setConfirmed(false);
  };

  const removeTier = (idx) => {
    setDraftTiers((prev) => {
      if (prev.length <= 1) return prev;  // No se puede dejar sin tramos
      const next = deepClone(prev);
      next.splice(idx, 1);
      // Si borramos el último cerrado (o el open), aseguramos que quede uno con max=null
      if (next[next.length - 1].max !== null) {
        next[next.length - 1] = { ...next[next.length - 1], max: null };
      }
      return next;
    });
    setConfirmed(false);
  };

  const addCustomPrice = () => {
    const p = Number(customPrice);
    if (!p || p <= 0) { toast.error("Precio inválido"); return; }
    if (customPrices.includes(p) || DEFAULT_SAMPLE_PRICES.includes(p)) {
      toast.info("Ese precio ya está en la lista");
      return;
    }
    setCustomPrices((prev) => [...prev, p].sort((a,b)=>a-b));
    setCustomPrice("");
  };

  const revert = () => {
    setDraftTiers(deepClone(savedTiers));
    setConfirmed(false);
    setValidationError(null);
    setPreview(null);
    setCustomPrices([]);
  };

  const save = async () => {
    if (!hasChanges || !confirmed || validationError) return;
    setSaving(true);
    try {
      await api.put("/admin/config", { key: "commission_tiers", value: draftTiers });
      toast.success("Comisiones actualizadas — aplican a publicaciones/checkouts nuevos");
      setSavedTiers(deepClone(draftTiers));
      setConfirmed(false);
      setCustomPrices([]);
    } catch (err) {
      const d = err?.response?.data?.detail || {};
      toast.error(d.message || "No se pudo guardar");
      setValidationError({ code: d.code, message: d.message, field: d.field });
    } finally { setSaving(false); }
  };

  if (loading) return <SectionShell title="Comisiones"><div className="text-neutral-500 text-sm">Cargando…</div></SectionShell>;

  const willGiveAway = preview?.will_give_away_prices || [];
  const canSave = hasChanges && !validationError && confirmed && !saving;

  return (
    <SectionShell title="Tramos de comisión" icon={DollarSign} testid="commission-tiers-section">
      <div className="space-y-4">
        <div className="text-xs text-neutral-400">
          Cada tramo aplica al <b className="text-white">precio de venta</b> por unidad. Se cobra el <b className="text-white">mayor</b> entre el % del precio y el mínimo del tramo. Aplica solo hacia adelante.
        </div>

        {/* Tabla editable */}
        <div className="overflow-x-auto border border-[#262626] rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-[#111]">
              <tr className="text-neutral-500 text-[10px] uppercase tracking-wider">
                <th className="py-2 px-3 text-left">Desde</th>
                <th className="py-2 px-3 text-left">Hasta</th>
                <th className="py-2 px-3 text-left">Comisión %</th>
                <th className="py-2 px-3 text-left">Mínimo $</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#262626]">
              {(draftTiers || []).map((t, i) => {
                const prevMax = i === 0 ? 0 : draftTiers[i - 1].max ?? 0;
                const errorHere = validationError?.field === `tiers[${i}].max`
                              || validationError?.field === `tiers[${i}].pct`
                              || validationError?.field === `tiers[${i}].min_fee`
                              || validationError?.field === `tiers[${i}]`;
                return (
                  <tr key={i} className={errorHere ? "bg-red-500/10" : ""}>
                    <td className="py-1.5 px-3 text-neutral-400 font-mono text-xs">${Number(prevMax).toFixed(2)}</td>
                    <td className="py-1.5 px-3">
                      {t.max === null ? (
                        <span className="text-neutral-400 font-mono text-xs">∞ (abierto)</span>
                      ) : (
                        <Input
                          type="number" min={0} step="0.01"
                          value={t.max ?? ""}
                          onChange={(e) => updateTier(i, "max", e.target.value)}
                          className="bg-[#171717] border-[#262626] h-8 w-24"
                          data-testid={`tier-${i}-max`}
                        />
                      )}
                    </td>
                    <td className="py-1.5 px-3">
                      <Input
                        type="number" min={0} max={100} step="0.01"
                        value={t.pct}
                        onChange={(e) => updateTier(i, "pct", e.target.value)}
                        className="bg-[#171717] border-[#262626] h-8 w-20"
                        data-testid={`tier-${i}-pct`}
                      />
                    </td>
                    <td className="py-1.5 px-3">
                      <Input
                        type="number" min={0} step="0.01"
                        value={t.min_fee}
                        onChange={(e) => updateTier(i, "min_fee", e.target.value)}
                        className="bg-[#171717] border-[#262626] h-8 w-24"
                        data-testid={`tier-${i}-min-fee`}
                      />
                    </td>
                    <td className="py-1.5 px-3 text-right">
                      <button
                        type="button"
                        onClick={() => removeTier(i)}
                        className="text-neutral-500 hover:text-red-400"
                        disabled={draftTiers.length <= 1}
                        title="Eliminar tramo"
                        data-testid={`tier-${i}-remove`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={addTier}
          className="border-[#262626] hover:bg-[#0A0A0A] h-8"
          data-testid="add-tier"
        >
          <Plus size={14} className="mr-1" /> Agregar tramo
        </Button>

        {/* Errores de validación */}
        {validationError && (
          <div className="bg-red-500/10 border border-red-500/40 rounded-md p-3 flex items-start gap-2" data-testid="tier-validation-error">
            <AlertTriangle size={14} className="text-red-400 mt-0.5 shrink-0" />
            <div className="text-xs">
              <div className="text-red-300 font-medium">{validationError.message}</div>
              {validationError.field && <div className="text-red-400/70 mt-0.5">Campo: <code>{validationError.field}</code></div>}
            </div>
          </div>
        )}

        {/* Previsualización */}
        {hasChanges && preview && !validationError && (
          <div className="border border-[#262626] rounded-md p-4 space-y-3" data-testid="tier-preview">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <Info size={14} className="text-blue-400" /> Impacto sobre próximas ventas
              </div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                Escenario transferencia · sin fee Payphone
              </div>
            </div>

            {/* Alerta "regala la carta" */}
            {willGiveAway.length > 0 && (
              <div className="bg-amber-500/15 border border-amber-500/50 rounded-md p-3" data-testid="give-away-alert">
                <div className="flex items-start gap-2 text-amber-200">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div className="text-xs space-y-1">
                    <div className="font-semibold">⚠ Advertencia — el vendedor recibiría $0 o menos:</div>
                    <ul className="list-disc ml-5">
                      {willGiveAway.map((g) => (
                        <li key={g.price}>
                          Carta de <b>${g.price.toFixed(2)}</b>: seller neto <b>${g.seller_net.toFixed(2)}</b>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-1">Puedes guardar igualmente, pero revisa que no sea un error.</div>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-neutral-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-1 px-2">Precio</th>
                    <th className="text-left py-1 px-2">Actual (comisión → seller)</th>
                    <th className="text-left py-1 px-2">Nueva (comisión → seller)</th>
                    <th className="text-left py-1 px-2">Δ seller</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626]">
                  {preview.samples.map((s) => (
                    <tr key={s.price} className={s.proposed_gives_away ? "bg-amber-500/10" : ""} data-testid={`preview-row-${s.price}`}>
                      <td className="py-1 px-2 font-mono">${s.price.toFixed(2)}</td>
                      <td className="py-1 px-2 text-neutral-400">${s.current.commission.toFixed(2)} ({s.current.pct}%) → ${s.current.seller_net.toFixed(2)}</td>
                      <td className="py-1 px-2">${s.proposed.commission.toFixed(2)} ({s.proposed.pct}%) → ${s.proposed.seller_net.toFixed(2)}</td>
                      <td className={"py-1 px-2 font-medium " + (s.delta_seller > 0 ? "text-emerald-400" : s.delta_seller < 0 ? "text-red-400" : "text-neutral-500")}>
                        {s.delta_seller === 0 ? "—" : `${s.delta_seller > 0 ? "+" : ""}$${s.delta_seller.toFixed(2)}`}
                        {s.delta_seller > 0 && <TrendingUp size={10} className="inline ml-1" />}
                        {s.delta_seller < 0 && <TrendingDown size={10} className="inline ml-1" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Probar con precio ad-hoc */}
            <div className="flex items-center gap-2 pt-2 border-t border-[#262626]">
              <Label className="text-xs text-neutral-500 shrink-0">Probar con precio:</Label>
              <Input
                type="number" min={0} step="0.01"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustomPrice()}
                placeholder="Ej: 1.75"
                className="bg-[#171717] border-[#262626] h-8 w-32 text-xs"
                data-testid="custom-price-input"
              />
              <Button type="button" onClick={addCustomPrice} variant="outline" className="border-[#262626] h-8 text-xs" data-testid="custom-price-add">
                Agregar
              </Button>
              {customPrices.length > 0 && (
                <button type="button" onClick={() => setCustomPrices([])} className="text-neutral-500 hover:text-white text-[10px] underline">
                  limpiar ({customPrices.length})
                </button>
              )}
            </div>
          </div>
        )}

        {/* Confirmación + Guardar */}
        {hasChanges && !validationError && (
          <div className="border-t border-[#262626] pt-4 space-y-3">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="tier-confirm-label">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 accent-[#EF4444]"
                data-testid="tier-confirm-checkbox"
              />
              <span className="text-sm text-neutral-300">
                Confirmo que estos tramos aplican solo a <b className="text-white">próximas</b> publicaciones y ventas. Las órdenes existentes conservan lo que el vendedor vio al aceptar.
              </span>
            </label>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={save}
                disabled={!canSave}
                className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50"
                data-testid="tier-save"
              >
                <BusyLabel busy={saving} text="Guardando…">Guardar cambios de comisión</BusyLabel>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={revert}
                className="border-[#262626] hover:bg-[#0A0A0A]"
                data-testid="tier-revert"
              >
                Descartar
              </Button>
            </div>
          </div>
        )}

        {!hasChanges && (
          <div className="text-xs text-neutral-500 flex items-center gap-1">
            <Check size={12} className="text-emerald-400" /> Sin cambios pendientes — estos son los valores en producción.
          </div>
        )}
      </div>
    </SectionShell>
  );
}

// ============================================================
// MIN PHOTOS PER PUBLICATION
// ============================================================

function MinPhotosSection() {
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/config/public");
        const v = r.data?.min_photos_per_publication ?? 2;
        setSaved(v); setDraft(v);
      } catch { toast.error("No se pudo cargar"); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    if (draft === saved) return;
    if (!Number.isInteger(draft) || draft < 1 || draft > 10) {
      toast.error("Debe ser un entero entre 1 y 10");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/config", { key: "min_photos_per_publication", value: draft });
      setSaved(draft);
      toast.success("Fotos mínimas actualizadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (loading) return <SectionShell title="Fotos mínimas"><div className="text-neutral-500 text-sm">Cargando…</div></SectionShell>;

  return (
    <SectionShell title="Fotos mínimas por publicación" icon={Camera} testid="min-photos-section">
      <div className="text-xs text-neutral-400 mb-3">
        Cantidad mínima de fotos que un vendedor debe subir al publicar una carta. Recomendado: 2 (frente y dorso).
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number" min={1} max={10} step={1}
          value={draft}
          onChange={(e) => setDraft(Number(e.target.value))}
          className="bg-[#171717] border-[#262626] h-9 w-24"
          data-testid="min-photos-input"
        />
        <Button
          type="button"
          onClick={save}
          disabled={draft === saved || saving}
          className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50"
          data-testid="min-photos-save"
        >
          <BusyLabel busy={saving} text="Guardando…">Guardar</BusyLabel>
        </Button>
        <span className="text-xs text-neutral-500">actual: <b className="text-white">{saved}</b></span>
      </div>
    </SectionShell>
  );
}

// ============================================================
// PARTNER STORES CTA
// ============================================================

function PartnerStoresSection() {
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState({ cta_url: "", cta_label: "" });
  const [draft, setDraft] = useState({ cta_url: "", cta_label: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/partner-stores");
        const cta = r.data?.cta || {};
        const val = { cta_url: cta.url || "", cta_label: cta.label || "" };
        setSaved(val); setDraft(val);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const hasChanges = saved.cta_url !== draft.cta_url || saved.cta_label !== draft.cta_label;

  const save = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      await api.put("/admin/partner-stores-settings", draft);
      setSaved({ ...draft });
      toast.success("Config de tiendas aliadas actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (loading) return <SectionShell title="Tiendas aliadas"><div className="text-neutral-500 text-sm">Cargando…</div></SectionShell>;

  return (
    <SectionShell title="Tiendas aliadas — CTA" icon={Store} testid="partner-stores-section">
      <div className="text-xs text-neutral-400 mb-3">
        Texto y destino del botón "Quiero ser tienda aliada" en la Home.
      </div>
      <div className="grid gap-3 max-w-md">
        <div>
          <Label className="text-xs text-neutral-500 uppercase tracking-wider">URL destino</Label>
          <Input
            value={draft.cta_url}
            onChange={(e) => setDraft((p) => ({ ...p, cta_url: e.target.value }))}
            placeholder="/register?tipo=tienda"
            className="bg-[#171717] border-[#262626] mt-1"
            data-testid="partner-cta-url"
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-500 uppercase tracking-wider">Texto del botón</Label>
          <Input
            value={draft.cta_label}
            onChange={(e) => setDraft((p) => ({ ...p, cta_label: e.target.value }))}
            placeholder="Quiero ser tienda aliada"
            className="bg-[#171717] border-[#262626] mt-1"
            data-testid="partner-cta-label"
          />
        </div>
        <div>
          <Button
            type="button"
            onClick={save}
            disabled={!hasChanges || saving}
            className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50"
            data-testid="partner-cta-save"
          >
            <BusyLabel busy={saving} text="Guardando…">Guardar</BusyLabel>
          </Button>
        </div>
      </div>
    </SectionShell>
  );
}

// ============================================================
// SHELL / HELPERS
// ============================================================

function SectionShell({ title, icon: Icon, children, testid }) {
  return (
    <section className="bg-[#0A0A0A] border border-[#262626] rounded-md p-5" data-testid={testid}>
      <h2 className="font-display text-lg mb-4 flex items-center gap-2">
        {Icon && <Icon size={16} className="text-[#EF4444]" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

function deepClone(x) { return JSON.parse(JSON.stringify(x)); }
